import axios, { type AxiosInstance } from 'axios';
import { createAccounts, loginHintKey, parkedFromKey, type AuthAccounts } from './accounts';
import { createAuthBroadcast, type AuthBroadcastEvent } from './broadcastSync';
import { AUTH_REQUEST_TIMEOUT_MS } from './config';
import { csrfHeaders } from './csrf';
import { createDeviceId } from './deviceId';
import { authEnvironment, safeGet, safeRemove, safeSet, type AuthEnvironment } from './environment';
import { createIsolatedTokenStore, type AuthIsolatedSession } from './isolatedSession';
import { decodeSignInMethod, decodeUserId } from './jwt';
import { runExclusive } from './locks';
import type { AuthNamespace } from './namespace';
import { realmAuthNamespaceRegistry, type AuthNamespaceRegistry } from './namespaceRegistry';
import { createPresenceReporter, type PresenceReporter } from './presence';
import { createRefreshClient } from './refreshClient';
import { createRefreshOrchestrator, REAUTH_REQUIRED_REASON, refreshFailureReason } from './refreshOrchestrator';
import { createRefreshScheduler } from './refreshScheduler';
import type { IAuthResponse } from './signIn';
import {
    createTokenStore,
    type AuthMode,
    type TokenChangeEvent,
    type TokenChangeListener,
    type TokenChangeOrigin,
    type TokenStore,
} from './tokenStore';

/** Почему сессия кончилась не по «Выйти». */
export type SessionEndReason =
    /** 401/403 ротации (кроме `server_error` и повторного входа через IdP) */
    | 'refresh-rejected'
    /**
     * 401 запроса без refresh у залогиненного, 401 повтора после удачной ротации (и любой 401 во вкладке
     * изолированного входа)
     */
    | 'unauthorized'
    /** выход в соседней вкладке */
    | 'foreign-logout';

/**
 * Решение по 401 запроса: `retry` — повторить (ротация удалась или соседняя вкладка уже сменила токен); `reject` —
 * отклонить (выход, если нужен, уже сделан); `unavailable` — ротация временно не удалась (сеть, 5xx), вход сохранён:
 * отказ — «сервер не ответил», а не «не авторизован».
 */
export type UnauthorizedDecision = 'retry' | 'reject' | 'unavailable';

/** Повторный вход через IdP, когда сервер отвечает на ротацию `reason: reauth_required` (потолок SAML-семейства). */
export interface AuthReauthOptions {
    /** На стенде включён вход через IdP (SAML в `authentication.controls.enabled`). Исключение — «выключен». */
    enabled(): boolean;
    /**
     * Адрес внутреннего SAML-редиректора с basename приложения. `currentPath` — pathname+search+hash страницы (с
     * basename); санитизирует его редиректор (`sanitizeReturnUrl`), ядро абсолютных адресов не собирает: basename
     * знает приложение.
     */
    redirectUrl(currentPath: string): string;
}

export interface AuthRuntimeOptions {
    /** Строки сессии приложения (ключи, канал, Web Lock, CSRF) — обязательны: на них держится изоляция приложений. */
    namespace: AuthNamespace;
    /** База Account-эндпоинтов и `/Presence` — `config.application.apiUrl`. */
    apiUrl: string;
    withCredentials: boolean;
    /** Без него `reauth_required` — обычный выход. */
    reauth?: AuthReauthOptions;
    /** Изолированный вход вкладки («от имени»): когда активен, обычная сессия этой вкладкой не читается и не пишется. */
    isolated?: AuthIsolatedSession;
    /**
     * Голый транспорт RefreshToken, SignOut и связки учёток — без интерцепторов клиента приложения; по умолчанию —
     * axios.
     */
    transport?: Pick<AxiosInstance, 'post' | 'get'>;
    /**
     * Заголовки приложения к SignOut (версия клиента, показ экрана поддержке): запрос идёт транспортом сессии, мимо
     * плагинов клиента. `Authorization` и CSRF ставит рантайм поверх. Исключение — без заголовков.
     */
    signOutHeaders?: () => Record<string, string>;
    /** Швы окружения для тестов; приложение не передаёт. */
    env?: Partial<AuthEnvironment>;
    /**
     * Реестр живых namespace (второй экземпляр с той же строкой — ошибка); по умолчанию — реестр окна. Тесту «вкладок»
     * в одном процессе — свой на вкладку.
     */
    registry?: AuthNamespaceRegistry;
}

/** Всё, что ядро делает со стором и UI приложения. Исключения и отказы колбэков ядро глотает. */
export interface AuthHooks {
    /** Профиль загружен (Redux `auth.isAuth`): отличает вход в соседней вкладке от ротации и решает, нужен ли выход. */
    isSignedIn: () => boolean;
    /** Сменился access (вход, ротация, соседняя вкладка, выход). Наружу — только access. */
    onAccessTokenChange?: (accessToken: string | null, origin: TokenChangeOrigin) => void;
    /** Сессия кончилась не по «Выйти»: токены уже сняты. */
    onSessionExpired: (reason: SessionEndReason) => void;
    /** Вход в соседней вкладке, пока здесь экран входа: загрузить профиль по токену. */
    onForeignLogin?: (accessToken: string) => void | Promise<void>;
    /**
     * Соседняя вкладка (канал или ротация общим refresh-cookie) принесла токен другого пользователя при загруженном
     * профиле: модульные кэши уже сброшены (`endSession`). По умолчанию — `onForeignLogin`.
     */
    onUserSwitch?: (accessToken: string) => void | Promise<void>;
    /** Стенд недоступен: 502/503/504, сеть, `server_error` ротации. */
    onApiUnavailable?: () => void;
}

export interface AuthRuntime {
    readonly namespace: AuthNamespace;

    /**
     * Подписки (канал вкладок, планировщик), таймер для уже лежащего токена, в cookie-режиме без access — одна тихая
     * ротация (промис ждёт её). Повторный вызов — тот же промис. Занятый namespace — `AuthNamespaceConflictError`.
     */
    start(hooks: AuthHooks): Promise<void>;
    /** Снять подписки, таймеры, канал, пульс и освободить namespace (тесты, HMR). */
    stop(): void;

    /** Вкладка изолированного входа («от имени»): обычные Account-запросы ей запрещены. */
    isIsolated(): boolean;
    getAccessToken(): string | null;
    getMode(): AuthMode;
    /** Режим сервера (SignInSettings): body→cookie переносит живой access в память и чистит localStorage. */
    setMode(mode: AuthMode): void;
    /** SignIn / SignInByHeader / SAML: режим → токены (`login`) → таймер. Профиль в стор кладёт приложение. */
    acceptLogin(response: Pick<IAuthResponse<unknown>, 'accessToken' | 'refreshToken' | 'authMode'>): void;
    /** События смены access этой вкладки — только access. */
    subscribe(listener: TokenChangeListener): () => void;
    getDeviceId(): string;
    /** Claim `signInMethod` текущего access (`SAML` — выход через IdP) или undefined. */
    getSignInMethod(): string | undefined;

    /** Ротация: в вкладке — один промис, между вкладками — Web Lock. true — access актуален. */
    refresh(rejectedAccessToken?: string | null): Promise<boolean>;
    /** Решение по 401 запроса (см. `UnauthorizedDecision`). */
    recoverFromUnauthorized(rejectedAccessToken: string | undefined): Promise<UnauthorizedDecision>;
    /**
     * Повтор запроса после ротации снова получил 401 с этим access: сервер не принимает и свежий токен — сессия
     * недействительна, выход (`onSessionExpired('unauthorized')`). Нет, если access уже сменился (ротация или вход
     * после повтора), пользователь не залогинен, идёт «Выйти» или страница показывает прежнего пользователя.
     */
    expireRejectedAccess(accessToken: string): void;

    /**
     * «Выйти»: сначала очистка и рассылка `logged-out` (упавший или зависший запрос токены не оставляет), затем —
     * после ротации в полёте — POST `/Account/SignOut` с прежним access (CSRF в cookie-режиме; 401 — ротация без
     * записи токенов и повтор). Отказ запроса — отказ промиса; в cookie-режиме неотозванное семейство отзывает
     * следующая ротация (в этой вкладке, соседней или после перезагрузки) вместо тихого входа.
     * Во вкладке изолированного входа — только очистка сеанса вкладки.
     */
    signOut(): Promise<void>;
    /**
     * Выход без запроса (SAML-выход через IdP, конец изолированного входа). `revoked: false` — семейство на сервере не
     * отозвано (запрос выхода через IdP упал): в cookie-режиме его отзовёт следующая ротация вместо тихого входа, как
     * после упавшего `signOut()`.
     */
    clearSession(options?: { revoked?: boolean }): void;

    /** Несколько учёток в одном браузере (мультиаккаунт): связка запаркованных учёток на сервере. */
    readonly accounts: AuthAccounts;

    /** Модульные кэши, которые не должны пережить смену пользователя. */
    onSessionEnd(reset: () => void): () => void;
    endSession(): void;

    /** Пульс «Сеансов»: POST `{apiUrl}/Presence` мимо интерцепторов; возвращает остановку (её делает и `stop()`). */
    startPresence(): () => void;
    /**
     * Страница показывает другого пользователя, чем тот, чей токен сейчас в сессии: переключение учётной записи (здесь
     * или в соседней вкладке) до перезагрузки. Запросы такой страницы — от прежнего пользователя, и токен нового им не
     * прикладывается (`authHeaders`), а клиент `/http` их не отправляет.
     */
    pageUserChanged(): boolean;
    /**
     * Токен для запросов мимо клиента `/http` (хабы SignalR, ссылки с `access_token`): как `getAccessToken`, но `null` у
     * страницы прежнего пользователя (`pageUserChanged`) — хаб не переподключится под чужой учётной записью.
     */
    pageAccessToken(): string | null;
    /** Для запросов мимо axios (keepalive): `Authorization`, если есть токен и он — пользователя страницы. */
    authHeaders(): Record<string, string>;
    /** Для клиента приложения: стенд недоступен — `hooks.onApiUnavailable`. */
    reportApiUnavailable(): void;
}

const statusOf = (error: unknown): number | undefined =>
    axios.isAxiosError(error) ? error.response?.status : undefined;

const safely = (task: () => void) => {
    try {
        task();
    } catch {
        // исключение колбэка приложения не ломает сессию и подписки
    }
};

/**
 * Сессия входа приложения: токены, ротация (single-flight во вкладке + Web Lock между вкладками), соседние вкладки,
 * выход, повторный вход через IdP, пульс присутствия. Создание ничего не делает — подписки и таймеры появляются в
 * `start()`. Экземпляр держит модуль приложения (`@/auth`).
 */
export function createAuthRuntime(options: AuthRuntimeOptions): AuthRuntime {
    const { namespace, apiUrl, withCredentials, reauth, isolated, transport } = options;
    const env = authEnvironment(options.env);
    const broadcast = createAuthBroadcast({ namespace, env });
    const tokenStore = createTokenStore({ namespace, broadcast, env });
    const isolatedStore = isolated ? createIsolatedTokenStore(isolated) : null;
    const deviceId = createDeviceId({ namespace, env });
    const client = createRefreshClient({ apiUrl, withCredentials, namespace, transport, env });

    const isIsolated = () => !!isolated?.active();
    const session = (): TokenStore => (isolatedStore && isIsolated() ? isolatedStore : tokenStore);

    const listeners = new Set<TokenChangeListener>();
    const sessionEndResets = new Set<() => void>();
    const presences = new Set<PresenceReporter>();
    let hooks: AuthHooks | null = null;
    let running: { started: Promise<void>; stop: () => void } | null = null;
    /** Пользователь access этой вкладки — сверка чужого токена. */
    let sessionUser: string | null = null;
    /**
     * Пользователь, чьи данные показывает страница. Переключение учётной записи его не меняет — страница уходит
     * перезагрузкой; до неё её запросы (автосохранение, `pagehide`) не должны уйти под чужим токеном.
     */
    let pageUser: string | null = null;
    /**
     * Страница уже показывает прежнего пользователя — до перезагрузки: ни ротация токена нового пользователя, ни выход
     * с повторным входом в соседней вкладке её не «переводят» — данные на экране и в формах остаются прежние.
     */
    let pageStale = false;
    /** Токен пришёл из `accounts.switchTo` этой вкладки. */
    let switchingAccount = false;
    let reauthInProgress = false;
    let revoking: Promise<void> | null = null;
    /** Идёт «Выйти»: токены уже сняты, а профиль в сторе — до конца запроса. */
    let signingOut = 0;
    /** Счётчик временных отказов ротации: решение по 401 узнаёт, что его ротация упала не окончательно. */
    let transientRefreshFailures = 0;

    /**
     * «Выйти» не дошёл до сервера в cookie-режиме: семейство refresh не отозвано, httpOnly-cookie жива, и тихая ротация
     * (в соседней вкладке или при следующей загрузке) вернула бы сессию — на общем компьютере чужому. Ключ — рядом с
     * запасной доставкой канала.
     */
    const pendingSignOutKey = `${namespace.broadcastChannel}__signout__`;
    /**
     * sessionStorage: эта вкладка ушла на IdP за повторным входом. Пока входа нет, тихая ротация не идёт и
     * `reauth_required` — обычный выход: refresh-cookie прежнего семейства жива и снова ответила бы `reauth_required`,
     * и редиректор с колбэком SAML уводили бы на IdP без конца.
     */
    const reauthStartedKey = `${namespace.broadcastChannel}__reauth__`;
    const accountKeys = [parkedFromKey(namespace), loginHintKey(namespace)];

    const signedIn = () => {
        try {
            return hooks?.isSignedIn() ?? false;
        } catch {
            return false;
        }
    };

    const expireSession = (reason: SessionEndReason) => {
        session().clearTokens('logout');
        safely(() => hooks?.onSessionExpired(reason));
    };

    /** Профиль по токену из соседней вкладки: вход там (`foreign`) или другой пользователь при живой сессии (`switch`). */
    const loadProfile = (kind: 'foreign' | 'switch', accessToken: string) =>
        safely(() => {
            const current = hooks;
            if (!current) return;
            const result =
                kind === 'switch' && current.onUserSwitch
                    ? current.onUserSwitch(accessToken)
                    : current.onForeignLogin?.(accessToken);
            void Promise.resolve(result).catch(() => undefined);
        });

    const reauthEnabled = (options: AuthReauthOptions) => {
        try {
            return options.enabled();
        } catch {
            return false;
        }
    };

    /** Уход на IdP: полный уход со страницы (не навигация роутера) обрывает запросы на уже негодных токенах. */
    const reauthenticate = (options: AuthReauthOptions) => {
        reauthInProgress = true;
        safeSet(env.sessionStorage, reauthStartedKey, '1');
        tokenStore.clearTokens('logout');
        env.location.assign(options.redirectUrl(env.location.currentPath()));
    };

    const withRefreshLock = <T>(task: () => Promise<T>) => runExclusive(env.locks, namespace.refreshLockName, task);

    const signOutRequestHeaders = (accessToken: string | null | undefined, csrf: Record<string, string>) => {
        let extra: Record<string, string> = {};
        try {
            extra = options.signOutHeaders?.() ?? {};
        } catch {
            // заголовки приложения необязательны: выход важнее
        }
        return { ...extra, ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}), ...csrf };
    };

    /** CSRF-заголовок SignOut: нужен только в cookie-режиме. */
    const signOutCsrf = (cookieMode: boolean) => (cookieMode ? csrfHeaders(namespace, env.cookies()) : {});

    const postSignOut = (accessToken: string | null | undefined, csrf: Record<string, string>) =>
        (transport ?? axios).post(`${apiUrl}/Account/SignOut`, undefined, {
            withCredentials,
            headers: signOutRequestHeaders(accessToken, csrf),
            timeout: AUTH_REQUEST_TIMEOUT_MS,
        });

    const orchestrator = createRefreshOrchestrator({
        namespace,
        tokens: tokenStore,
        client,
        deviceId,
        env,
        onRejected(error) {
            // После stop() решения ротации в полёте не применяются: экземпляр молчит.
            if (!running) return;
            if (reauth && refreshFailureReason(error) === REAUTH_REQUIRED_REASON && reauthEnabled(reauth)) {
                // Уход на IdP этой загрузки уже идёт — параллельные отказы его не повторяют. Начатый до перезагрузки и
                // не закончившийся входом — не повод уйти снова: вкладка выходит.
                if (reauthInProgress) return;
                if (!safeGet(env.sessionStorage, reauthStartedKey)) {
                    reauthenticate(reauth);
                    return;
                }
            }
            expireSession('refresh-rejected');
        },
        onApiUnavailable: () => runtime.reportApiUnavailable(),
        onTransientFailure: () => {
            transientRefreshFailures += 1;
        },
        revoke: (accessToken) => postSignOut(accessToken, signOutCsrf(tokenStore.getMode() === 'cookie')),
    });

    const scheduler = createRefreshScheduler({
        getAccessToken: () => tokenStore.getAccessToken(),
        refresh: () => runtime.refresh(),
        env,
    });

    const onSessionTokens = ({ accessToken, origin }: TokenChangeEvent) => {
        if (!isIsolated()) {
            if (accessToken && origin !== 'logout') scheduler.schedule(accessToken);
            else scheduler.cancel();
            // Сессия снова есть (вход, ротация, соседняя вкладка) — повторный вход через IdP закончен.
            if (accessToken) safeRemove(env.sessionStorage, reauthStartedKey);
        }
        safely(() => hooks?.onAccessTokenChange?.(accessToken, origin));

        const previousUser = sessionUser;
        sessionUser = accessToken ? (decodeUserId(accessToken) ?? null) : null;
        // Токен другого пользователя не по своему входу — не тихая ротация. Без claim (старый сервер, тест) — как
        // раньше: сверять нечего.
        const foreign = origin === 'refresh' || origin === 'broadcast';
        const switched = !!accessToken && foreign && !!previousUser && !!sessionUser && sessionUser !== previousUser;
        // Пользователь страницы — тот, чей профиль на экране. Пока он загружен (`signedIn`), очистка токенов его не
        // сбрасывает, а токен другого пользователя (ротация, вход в соседней вкладке или здесь же) делает страницу
        // страницей прежнего до перезагрузки. Исключение — без мультиаккаунта чужой вход принимается на месте (сброс
        // стора, R9). Без профиля (экран входа, профиль ещё не загружен) показывать нечего — страница нового.
        if (!pageStale) {
            if (!sessionUser) {
                if (!signedIn()) pageUser = null;
            } else if (!pageUser || sessionUser === pageUser) {
                pageUser = sessionUser;
            } else if (!signedIn() || (!switchingAccount && !runtime.accounts.enabled())) {
                pageUser = sessionUser;
            } else {
                pageStale = true;
            }
        }
        // С мультиаккаунтом и пока профиль прежнего пользователя ещё грузится: иначе страница не узнает о смене и
        // осталась бы с отменяемыми запросами (`pageUserChanged`) без перезагрузки.
        if (switched && (signedIn() || runtime.accounts.enabled())) {
            runtime.endSession();
            loadProfile('switch', accessToken);
        }
    };

    const emit = (event: TokenChangeEvent) => {
        onSessionTokens(event);
        listeners.forEach((listener) => safely(() => listener(event)));
    };
    tokenStore.subscribe(emit);
    isolatedStore?.subscribe(emit);

    const onBroadcast = (event: AuthBroadcastEvent) => {
        // Режим сервера могла сменить соседняя вкладка (SignInSettings после переключения на cookie): событие
        // применяется в её режиме, иначе access cookie-режима лёг бы в localStorage.
        const storedMode = safeGet(env.storage, namespace.authModeKey);
        if ((storedMode === 'body' || storedMode === 'cookie') && storedMode !== tokenStore.getMode()) {
            tokenStore.setMode(storedMode);
        }
        if (event.type === 'logged-out') {
            tokenStore.clearTokens('broadcast');
            safely(() => hooks?.onSessionExpired('foreign-logout'));
            return;
        }
        // Идёт «Выйти» этой вкладки: результат ротации, которую соседняя вкладка начала до выхода и записала раньше, чем
        // узнала о нём, сюда не принимается.
        if (signingOut) return;
        // До записи: вход в соседней вкладке отличается от тихой ротации тем, что здесь профиль не загружен.
        const wasSignedIn = signedIn();
        if (event.type === 'tokens-updated') {
            tokenStore.setTokens(
                { accessToken: event.accessToken || null, refreshToken: event.refreshToken || null },
                'broadcast',
            );
        } else if (tokenStore.getMode() === 'body') {
            // Запасная доставка без токенов: отправитель уже записал их в общий localStorage.
            tokenStore.setTokens(tokenStore.getTokens(), 'broadcast');
        } else {
            // Cookie-режим: refresh-cookie у вкладок общий, access нужен этой вкладке только на экране входа. Без Web
            // Locks не ротируем: событие получают все вкладки сразу, параллельные POST одной cookie сервер считает reuse
            // и выкидывает все (вход подхватит перезагрузка).
            if (!wasSignedIn && env.locks) {
                void runtime.refresh().then((ok) => {
                    const accessToken = tokenStore.getAccessToken();
                    if (ok && accessToken && !signedIn()) loadProfile('foreign', accessToken);
                });
            }
            return;
        }
        const accessToken = tokenStore.getAccessToken();
        if (!wasSignedIn && accessToken) loadProfile('foreign', accessToken);
    };

    /**
     * Незавершённый выход: голая ротация (без записи токенов и рассылки — иначе вкладка на экране входа вошла бы по
     * воскрешённой сессии) даёт access, им SignOut отзывает семейство. Под замком ротации и с перепроверкой отметки:
     * выход, который ещё идёт в соседней вкладке, или её отзыв мог уже закончиться. Ротация отклонена (401/403) — cookie
     * уже мертва, отзывать нечего; сеть, 5xx — повтор при следующей ротации. Вкладка в любом случае не входит.
     */
    const revokePendingSignOut = (): Promise<void> => {
        revoking ??= withRefreshLock(async () => {
            if (!safeGet(env.storage, pendingSignOutKey)) return;
            try {
                const { accessToken } = await client.postRefreshToken(null, deviceId.get(), 'cookie');
                await postSignOut(accessToken, signOutCsrf(true));
            } catch (error) {
                const status = statusOf(error);
                if (status !== 401 && status !== 403) return;
            }
            safeRemove(env.storage, pendingSignOutKey);
        }).finally(() => {
            revoking = null;
        });
        return revoking;
    };

    const runtime: AuthRuntime = {
        namespace,

        start(nextHooks) {
            if (running) return running.started;
            const release = (options.registry ?? realmAuthNamespaceRegistry()).claim(namespace);
            hooks = nextHooks;
            const teardown: Array<() => void> = [];
            const isolatedTab = isIsolated();
            if (!isolatedTab) {
                broadcast.start();
                teardown.push(broadcast.subscribe(onBroadcast), () => broadcast.stop());
                scheduler.start();
                teardown.push(() => scheduler.stop());
            }
            const accessToken = session().getAccessToken();
            sessionUser = accessToken ? (decodeUserId(accessToken) ?? null) : null;
            if (!pageStale) pageUser = sessionUser;
            let started = Promise.resolve();
            if (!isolatedTab && accessToken) {
                scheduler.schedule(accessToken);
            } else if (
                !isolatedTab &&
                tokenStore.getMode() === 'cookie' &&
                !safeGet(env.sessionStorage, reauthStartedKey)
            ) {
                // Cookie-режим после перезагрузки: access в памяти потерян, refresh-cookie может быть жив. Незавершённый
                // «Выйти» — не повод войти: ротация отзывает семейство, вкладка остаётся на экране входа.
                started = runtime.refresh().then(
                    () => undefined,
                    () => undefined,
                );
            }
            running = {
                started,
                stop() {
                    teardown.forEach((off) => off());
                    release();
                },
            };
            return started;
        },

        stop() {
            presences.forEach((presence) => presence.stop());
            presences.clear();
            if (!running) return;
            running.stop();
            running = null;
            hooks = null;
            reauthInProgress = false;
        },

        isIsolated,
        getAccessToken: () => session().getAccessToken(),
        getMode: () => session().getMode(),
        setMode: (mode) => session().setMode(mode),

        acceptLogin(response) {
            const store = session();
            // Явный вход отменяет незавершённый выход и повторный вход через IdP: новое семейство — его.
            if (!isIsolated()) {
                safeRemove(env.storage, pendingSignOutKey);
                safeRemove(env.sessionStorage, reauthStartedKey);
                accountKeys.forEach((key) => safeRemove(env.sessionStorage, key));
            }
            // Режим — до записи токенов: иначе setTokens пишет не в то хранилище. Сервер мог переключить режим сам.
            store.setMode(response.authMode || 'body');
            store.setTokens(
                { accessToken: response.accessToken, refreshToken: response.refreshToken || null },
                'login',
            );
        },

        subscribe(listener) {
            listeners.add(listener);
            return () => {
                listeners.delete(listener);
            };
        },

        getDeviceId: () => deviceId.get(),

        getSignInMethod() {
            const accessToken = runtime.getAccessToken();
            return accessToken ? decodeSignInMethod(accessToken) : undefined;
        },

        refresh(rejectedAccessToken) {
            // Во вкладке изолированного входа обычная ротация запрещена: продление — своим эндпоинтом приложения.
            if (isIsolated()) return Promise.resolve(false);
            // Незавершённый «Выйти» (здесь, в соседней вкладке или до перезагрузки): ротация вернула бы сессию —
            // семейство отзывается, вкладка не входит.
            if (tokenStore.getMode() === 'cookie' && safeGet(env.storage, pendingSignOutKey)) {
                return revokePendingSignOut().then(() => false);
            }
            return orchestrator.refresh(rejectedAccessToken);
        },

        async recoverFromUnauthorized(rejectedAccessToken) {
            const wasSignedIn = signedIn();
            const accessBefore = runtime.getAccessToken();
            // Запрос, начатый под прежним пользователем, токеном другого (переключение учётной записи) не повторяется.
            const sameUser = (accessToken: string | null) => {
                const before = rejectedAccessToken ? decodeUserId(rejectedAccessToken) : undefined;
                const now = accessToken ? decodeUserId(accessToken) : undefined;
                return !before || !now || before === now;
            };
            const retryAs = (accessToken: string | null): UnauthorizedDecision =>
                sameUser(accessToken) ? 'retry' : 'reject';
            const transientBefore = transientRefreshFailures;
            if (await runtime.refresh(rejectedAccessToken)) return retryAs(runtime.getAccessToken());
            const accessNow = runtime.getAccessToken();
            // Соседняя вкладка сменила токен, пока шла наша ротация.
            if (accessNow && accessNow !== accessBefore) return retryAs(accessNow);
            // Cookie-режим: выход уже решила ротация, прочее — временный отказ. Body с refresh — временный отказ.
            if (runtime.getMode() === 'cookie' || session().getRefreshToken())
                return transientRefreshFailures !== transientBefore ? 'unavailable' : 'reject';
            // Без refresh (bootstrap): выходим, только если пользователь был и остался залогинен — иначе 401 запроса
            // профиля при параллельном входе стёр бы только что записанный access. Во время «Выйти» это не истечение
            // сессии: её снимает сам пользователь.
            if (isIsolated() || (wasSignedIn && signedIn() && !signingOut)) expireSession('unauthorized');
            return 'reject';
        },

        expireRejectedAccess(accessToken) {
            if (runtime.getAccessToken() !== accessToken || runtime.pageUserChanged()) return;
            if (isIsolated() || (signedIn() && !signingOut)) expireSession('unauthorized');
        },

        async signOut() {
            if (isIsolated()) {
                runtime.clearSession();
                return;
            }
            const cookieMode = tokenStore.getMode() === 'cookie';
            const { accessToken, refreshToken } = tokenStore.getTokens();
            const csrf = signOutCsrf(cookieMode);
            // Семейство отзывает запрос ниже; пока он не дошёл, ротация вернула бы сессию.
            if (cookieMode) safeSet(env.storage, pendingSignOutKey, '1');
            tokenStore.clearTokens('logout');
            signingOut += 1;
            try {
                // Ротация, начатая до выхода (здесь или в соседней вкладке), завершается раньше запроса: её результат
                // отзывается, а не пишется (поколение сессии сменилось).
                await withRefreshLock(async () => {
                    try {
                        await postSignOut(accessToken, csrf);
                    } catch (error) {
                        // Протухший access: сервер отзывает семейство refresh по claim токена — без повтора оно
                        // осталось бы живым. Ротация — голая: токены уже сняты и соседям не рассылаются.
                        if (statusOf(error) !== 401 || (!cookieMode && !refreshToken)) throw error;
                        let next: { accessToken: string };
                        try {
                            next = await client.postRefreshToken(
                                refreshToken,
                                deviceId.get(),
                                cookieMode ? 'cookie' : 'body',
                            );
                        } catch {
                            throw error;
                        }
                        await postSignOut(next.accessToken, signOutCsrf(cookieMode));
                    }
                    // Под замком: отзыв в соседней вкладке, ждущий его, видит уже снятую отметку.
                    if (cookieMode) safeRemove(env.storage, pendingSignOutKey);
                });
            } finally {
                signingOut -= 1;
            }
        },

        accounts: createAccounts({
            apiUrl,
            withCredentials,
            namespace,
            transport,
            env,
            tokens: tokenStore,
            deviceId,
            isIsolated,
            withRefreshLock,
            acceptLogin: (response) => {
                switchingAccount = true;
                try {
                    runtime.acceptLogin(response);
                } finally {
                    switchingAccount = false;
                }
            },
            revoke: (accessToken) => postSignOut(accessToken, signOutCsrf(tokenStore.getMode() === 'cookie')),
            pendingSignOut: () => tokenStore.getMode() === 'cookie' && !!safeGet(env.storage, pendingSignOutKey),
            revokePendingSignOut,
        }),

        clearSession({ revoked = true } = {}) {
            if (!revoked && !isIsolated() && tokenStore.getMode() === 'cookie') {
                safeSet(env.storage, pendingSignOutKey, '1');
            }
            session().clearTokens('logout');
        },

        onSessionEnd(reset) {
            sessionEndResets.add(reset);
            return () => {
                sessionEndResets.delete(reset);
            };
        },

        endSession() {
            sessionEndResets.forEach((reset) => safely(reset));
        },

        startPresence() {
            const presence = createPresenceReporter({
                apiUrl,
                getAccessToken: () => runtime.pageAccessToken(),
                hiddenIntervalMs: isIsolated() ? isolated?.hiddenBeatIntervalMs : undefined,
                env,
            });
            presence.start();
            presences.add(presence);
            return () => {
                presence.stop();
                presences.delete(presence);
            };
        },

        pageUserChanged() {
            if (isIsolated()) return false;
            if (pageStale) return true;
            if (!pageUser) return false;
            // Токен — в момент вызова: в body-режиме соседняя вкладка пишет localStorage раньше, чем дойдёт событие.
            const accessToken = session().getAccessToken();
            const currentUser = accessToken ? decodeUserId(accessToken) : undefined;
            return !!currentUser && currentUser !== pageUser;
        },

        pageAccessToken: () => (runtime.pageUserChanged() ? null : runtime.getAccessToken()),

        authHeaders(): Record<string, string> {
            const accessToken = runtime.pageAccessToken();
            return accessToken ? { Authorization: `Bearer ${accessToken}` } : {};
        },

        reportApiUnavailable: () => safely(() => hooks?.onApiUnavailable?.()),
    };

    return runtime;
}
